import React from 'react';
import { FileText, Image as ImageIcon, Video, Music, Archive, Trash2, RefreshCcw } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../utils/api';

const MediaFileCard = ({ file, onRefresh }) => {
  const { user } = useAuth();
  const canArchive = ['manager', 'super-admin'].includes(user?.role);

  const handleArchiveToggle = async (e) => {
    e.stopPropagation();
    const action = file.isArchived ? 'unarchive' : 'archive';
    if (!window.confirm(`Are you sure you want to ${action} ${file.fileName}?`)) return;
    
    try {
      if (file.isArchived) {
        // Unarchive logic (just update isArchived to false)
        const res = await api(`/media-library/${file._id}`, {
          method: 'PATCH',
          body: { isArchived: false }
        });
        if (res.ok) onRefresh();
      } else {
        const res = await api(`/media-library/${file._id}`, { method: 'DELETE' });
        if (res.ok) onRefresh();
      }
    } catch (err) {
      alert(`Failed to ${action} file`);
    }
  };

  const handlePermanentDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`CRITICAL WARNING: Are you sure you want to PERMANENTLY delete ${file.fileName} from the server? This cannot be undone and usage analytics will be lost.`)) return;
    
    try {
      const res = await api(`/media-library/${file._id}/permanent`, { method: 'DELETE' });
      if (res.ok) {
        onRefresh();
      }
    } catch (err) {
      alert('Failed to delete file permanently');
    }
  };

  const renderIcon = () => {
    switch (file.fileType) {
      case 'image': return <ImageIcon size={32} color="#3b82f6" />;
      case 'video': return <Video size={32} color="#ef4444" />;
      case 'audio': return <Music size={32} color="#8b5cf6" />;
      default: return <FileText size={32} color="#10b981" />;
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  return (
    <div 
      style={{ 
        background: 'white', 
        borderRadius: '12px', 
        border: '1px solid var(--color-border-primary)', 
        overflow: 'hidden',
        position: 'relative',
        transition: 'box-shadow 0.2s, transform 0.2s',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'none';
      }}
      onClick={() => window.open(file.fileUrl, '_blank')}
    >
      <div style={{ height: '140px', background: 'var(--color-background-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        {file.fileType === 'image' ? (
          <img src={file.fileUrl} alt={file.fileName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          renderIcon()
        )}
        
        {canArchive && (
          <div style={{ position: 'absolute', top: '8px', right: '8px', display: 'flex', gap: '8px' }}>
            <button 
              onClick={handleArchiveToggle}
              style={{ background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: file.isArchived ? 'var(--color-brand-primary)' : 'var(--color-text-warning)', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
              title={file.isArchived ? "Unarchive File" : "Archive File"}
            >
              {file.isArchived ? <RefreshCcw size={16} /> : <Archive size={16} />}
            </button>
            <button 
              onClick={handlePermanentDelete}
              style={{ background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--color-text-error)', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
              title="Permanent Delete"
            >
              <Trash2 size={16} />
            </button>
          </div>
        )}
      </div>
      
      <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={file.fileName}>
            {file.fileName}
          </h3>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <span style={{ fontSize: '12px', background: 'var(--color-background-tertiary)', padding: '2px 8px', borderRadius: '12px', color: 'var(--color-text-secondary)' }}>
            {file.category}
          </span>
          <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
            {formatSize(file.fileSize)}
          </span>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--color-border-primary)', paddingTop: '12px', fontSize: '12px' }}>
          <div style={{ color: 'var(--color-text-tertiary)' }}>
            Added by {file.uploadedByName || 'User'}
          </div>
          <div style={{ color: 'var(--color-brand-primary)', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}>
            Used: {file.usageCount}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaFileCard;
